<script setup>
import { defineProps, ref } from 'vue';
import { OFFER_STATUS } from '@/utils/enum/offerStatus';
import { Icons, Modal } from '@n11/so-ui-components';
import { rejectOffer } from '../services/productDealService';

const revokeModalRef = ref(null);
const isLoading = ref(false);

const props = defineProps({
  upperPrice: {
    type: Number,
    required: true,
    default: 300,
  },
  lowerPrice: {
    type: Number,
    required: true,
    default: 260,
  },
  offerPrice: {
    type: Number,
    required: false,
    default: null,
  },
  commission: {
    type: Number,
    required: true,
    default: 9,
  },
  // Same type with OfferStatus. Because it is determined according to offers.
  dealStatus: {
    type: String,
    required: true,
    default: OFFER_STATUS.WAITING_FOR_ACCEPTANCE,
  },
  offerStatus: {
    type: String,
    required: true,
    default: OFFER_STATUS.WAITING_FOR_ACCEPTANCE,
  },
  startDate: {
    type: Date,
    required: false,
    default: null,
  },
  endDate: {
    type: Date,
    required: false,
    default: null,
  },
  plannedStartDate: {
    type: Date,
    required: false,
    default: null,
  },
  plannedEndDate: {
    type: Date,
    required: false,
    default: null,
  },
  pid: {
    type: Number,
    required: true,
    default: null,
  },
  dealId: {
    type: Number,
    required: true,
    default: null,
  },
  groupId: {
    type: Number,
    required: true,
    default: null,
  },
  displayPrice: {
    type: Number,
    required: true,
    default: null,
  },
  categoryId: {
    type: Number,
    required: true,
    default: null,
  },
  hasVariants: {
    type: Boolean,
    required: true,
  },
});

const formatPrice = (price) => {
  const priceFormat = {
    style: 'currency',
    currency: 'TRY',
  };

  return price.toLocaleString('tr-TR', priceFormat);
};

const formatDate = (date) => {
  const dateOptions = {
    day: 'numeric',
    month: 'long',
  };
  const timeOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };
  const lastDate = new Date(date);

  return `
    ${lastDate.toLocaleString('tr-TR', dateOptions)},
    ${lastDate.toLocaleString('tr-TR', timeOptions)}
  `;
};

const isDisabled = () => {
  const { dealStatus, lowerPrice, displayPrice } = props;
  if (dealStatus === OFFER_STATUS.REFUSED) return true;
  if (
    dealStatus === OFFER_STATUS.ACCEPTED ||
    dealStatus === OFFER_STATUS.SUPERSEDED
  ) {
    return displayPrice < lowerPrice;
  }
  return false;
};

const isSelected = () => {
  const { dealStatus, upperPrice, lowerPrice, displayPrice } = props;
  return (
    (dealStatus === OFFER_STATUS.ACCEPTED ||
      dealStatus === OFFER_STATUS.SUPERSEDED) &&
    displayPrice <= upperPrice &&
    displayPrice >= lowerPrice
  );
};

const isSuperseded = () => {
  const { dealStatus, startDate, endDate, displayPrice, lowerPrice } = props;
  return (
    dealStatus === OFFER_STATUS.SUPERSEDED &&
    startDate &&
    endDate &&
    displayPrice < lowerPrice
  );
};

const isRefused = () => props.offerStatus === OFFER_STATUS.REFUSED;

const isPlanned = () => props.offerStatus === OFFER_STATUS.WAITING_FOR_APPLYING;

const isNotSelectedAndUpper = () => {
  const { startDate, endDate, displayPrice, lowerPrice } = props;
  return !startDate && !endDate && displayPrice < lowerPrice;
};

const handleRejectOffer = async () => {
  isLoading.value = true;
  
  try {
    await rejectOffer({
      dealId: props.dealId,
      sellerId: props.sellerId,
      groupId: props.groupId,
      displayPrice: props.displayPrice,
      categoryId: props.categoryId,
      productId: props.pid,
    });
    window.location.reload();
  } catch (error) {
    isLoading.value = false;
    revokeModalRef.value.hiddenModal();
  }
}

const revokeOfferclick = () => {
  revokeModalRef.value.openModal();
};
</script>

<template>
  <div :class="['offer', { disabled: isDisabled(), selected: isSelected(), planned: isPlanned() }]">
    <p class="offer-price-interval">
      {{ formatPrice(props.upperPrice) }} - {{ formatPrice(props.lowerPrice) }}
    </p>
    <p class="offer-commission">%{{ props.commission }} Komisyon</p>
    <div v-if="isRefused()" class="offer-refused">
      <p>Tekliften vazgeçtiniz</p>
      <div>
        <span>Tekliften yararlandığınız süre:</span>
        <span>
          {{ formatDate(props.startDate) }} - {{ formatDate(props.endDate) }}
        </span>
      </div>
    </div>
    <div v-else-if="isSelected()">
      <Icons name="TickCircle" color="#1C1C1E" class="tick" />
      <p class="price">
        <Icons name="Sale" color="#FF44EE" /> Satış Fiyatı:
        <b>{{ formatPrice(props.displayPrice) }}</b>
      </p>
      <p class="date">
        <span>Teklifin geçerlilik tarihi:</span>
        <span>
          {{ formatDate(startDate) }} - {{ formatDate(plannedEndDate) }}
        </span>
      </p>
      <button type="button" @click="revokeOfferclick">
        Tekliften Vazgeç <Icons name="RightArrow" color="#FF44EE" />
      </button>
    </div>
    <p v-else-if="isSuperseded()" class="offer-superseded">
      <span>Tekliften yararlandığınız süre:</span>
      <br />
      <span>
        {{ formatDate(props.startDate) }} - {{ formatDate(props.endDate) }}
      </span>
    </p>
    <p v-else-if="isNotSelectedAndUpper()" class="offer-disabled-reason">
      Daha yüksek fiyatlı tekliften <br />
      yararlanamazsınız.
    </p>

    <div v-else-if="isPlanned()">
      <Icons name="TickCircle" color="#1C1C1E" class="tick" />
      <p class="price">
        <Icons name="Sale" color="#FF44EE" />Planlanan Satış Fiyatı:
        <b v-if="offerPrice">{{ formatPrice(offerPrice) }}</b>
      </p>
      <p class="date">
        <span>Teklifin geçerlilik tarihi:</span>
        <span>
          {{ formatDate(plannedStartDate) }} - {{ formatDate(plannedEndDate) }}
        </span>
      </p>
      <div class="revoke-planned-btn">
        <p class="text">
         <Icons name="Clock" class="clock"/>
          <span>Planlandı</span>
        </p>
        <button type="button" @click="revokeOfferclick">
          Tekliften Vazgeç <Icons name="RightArrow" color="#FF44EE" />
        </button>
      </div>
    </div>

    <Modal
      ref="revokeModalRef"
      title="Tekliften vazgeçmek istediğinize emin misiniz?"
      negativeActionText="Geri Dön"
      positiveActionText="Tekliften Vazgeç"
      :isPositiveActionButtonDisabled="isLoading"
      :isPositiveActionPending="isLoading"
      @onPositiveActionClick="handleRejectOffer"
      @onNegativeActionClick="$refs.revokeModalRef.hiddenModal()"
    >
      <template v-slot:body>
        <div class="revoke-body">
          <p v-if="hasVariants">
            Komisyon teklifi <span class="all-variants__bold">ürünün tüm varyantları</span> 
            için iptal edilecek ve başka bir tekliften yararlanamayacaksınız.
          </p>
          <p v-else>
            Komisyon teklifi iptal edilecek ve bu ürün için başka bir <br />
            tekliften yararlanamayacaksınız.
          </p>
          <div class="description">
            Bu teklif, yalnızca belirtilen tarihler arasında ve tek sefer için
            geçerlidir. Tekliften bir kez vazgeçildiğinde tekrar yararlanılamaz,
            teklif koşullarındaki fiyat ve komisyon oranı geçerliliğini yitirir.
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/variables';
@import '@/assets/styles/utilities';

.offer {
  display: flex;
  padding: 16px 12px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  height: 100%;
  position: relative;

  &-commission {
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid $colorD20;
    color: $colorD50;
    font-size: 11px;
    font-weight: 600;
    line-height: 14px;
  }

  &-price-interval {
    color: $colorN80;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    letter-spacing: -0.05px;
  }

  &-superseded {
    span {
      color: $colorD50;
      font-size: 10px;
      font-weight: 600;
      line-height: 12px;
    }
  }

  &-refused {
    margin-top: 8px;

    p {
      color: $colorE50;
      font-size: 11px;
      font-weight: 600;
      line-height: 14px;
    }

    div {
      margin-top: 8px;
      display: flex;
      flex-direction: column;
      gap: 2px;

      span {
        display: block;
        color: $colorD50;
        font-size: 10px;
        font-weight: 600;
        line-height: 12px;
      }
    }
  }

  &.planned{
    background-color: $colorM05;
    .icon.tick {
      position: absolute;
      top: 16px;
      right: 12px;
    }

    .offer-commission {
      border: 1px solid $colorP20;
      color: $colorM50;
      background-color: $colorN00;
    }

    p {
      &.price {
        display: flex;
        color: $colorM50;
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;

        b {
          margin-left: 4px;
          color: $colorM50;
          font-size: 14px;
        }
      }

      &.date {
        margin-top: 8px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        color: $colorD50;
        font-size: 10px;
        font-weight: 600;
        line-height: 12px;

        span {
          display: block;
        }
      }

      &.text{
        padding: 6px;
        border-radius: 4px;
        border: 0.5px solid $colorD30;
        background: $colorY05;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
          span{
            color: $colorY60;
            font-size: 11px;
            font-weight: 600;
            line-height: 14px;
          }
      }
    }

    .revoke-planned-btn{
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 12px;
      margin-top: 8px;
    }

    button {
      padding: 0;
      display: flex;
      color: $colorM50;
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
    }
  }

  &.selected {
    background-color: $colorM05;
    .icon.tick {
      position: absolute;
      top: 16px;
      right: 12px;
    }

    .offer-commission {
      border: 1px solid $colorM50;
      color: $colorM50;
      background-color: $colorN00;
    }

    p {
      &.price {
        display: flex;
        color: $colorM50;
        font-size: 12px;
        font-weight: 600;
        line-height: 16px;

        b {
          margin-left: 4px;
          color: $colorM50;
          font-size: 14px;
        }
      }

      &.date {
        margin-top: 8px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        color: $colorD50;
        font-size: 10px;
        font-weight: 600;
        line-height: 12px;

        span {
          display: block;
        }
      }
    }

    button {
      padding: 0;
      display: flex;
      margin-top: 8px;
      color: $colorM50;
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
    }
  }

  &.disabled {
    background: $colorD05;

    .offer-price-interval,
    .offer-commission,
    .offer-disabled-reason {
      color: $colorD40;
      background-color: $colorN00;
    }

    .offer-disabled-reason {
      font-size: 11px;
      font-weight: 400;
      line-height: 14px;
    }
  }

  .revoke-body {
    gap: 16px;
    display: flex;
    flex-direction: column;

    p {
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;
      letter-spacing: -0.05px;
      color: $colorN80;
    }

    .change-offer {
      display: flex;
      padding-top: 10px;
      align-items: center;
      gap: 8px;
      align-self: stretch;

      > div {
        position: relative;
        display: flex;
        padding: 24px 24px 16px 24px;
        flex-direction: column;
        flex: 1 0 0;
        align-self: stretch;
        border-radius: 6px;
        border: 1px solid $colorD10;
        align-items: center;

        .title {
          width: max-content;
          display: flex;
          gap: 2px;
          padding: 4px 8px;
          justify-content: center;
          align-items: center;
          position: absolute;
          left: 50%;
          -webkit-transform: translateX(-50%);
          transform: translateX(-50%);
          top: -9px;
          border-radius: 6px;
          border: 0.5px solid $colorD20;
          background: $colorD10;
          color: $colorD50;
          font-size: 10px;
          font-weight: 600;
          line-height: 12px;
        }

        .price {
          color: var(--Neutral---Background-N80, #242424);
          font-size: 16px;
          font-weight: 700;
          line-height: 20px;
          letter-spacing: -0.05px;
        }

        .commission {
          display: flex;
          height: 18px;
          padding: 2px 4px;
          align-items: center;
          gap: 4px;
          border-radius: 4px;
          border: 1px solid $colorD20;
          background: $colorN00;
          color: $colorD50;
          text-align: center;
          font-size: 10px;
          font-weight: 600;
          line-height: 12px;
          margin-top: 4px;
        }

        &.change-offer-new {
          border: 1px solid $colorP20;
          background: $colorM05;

          .title {
            color: white;
            background: $colorM50;

            &::before {
              display: block;
              content: '';
              background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' fill='none' xmlns:v='https://vecta.io/nano'%3E%3Cpath d='M6.596 1.669a.44.44 0 0 1 .808 0l1.345 3.234c.063.151.205.255.369.268l3.491.28a.44.44 0 0 1 .25.768l-2.66 2.278c-.125.107-.179.274-.141.434l.813 3.407a.44.44 0 0 1-.654.475l-2.989-1.825c-.14-.086-.316-.086-.456 0l-2.989 1.826a.44.44 0 0 1-.654-.475l.813-3.407c.038-.16-.016-.327-.141-.434l-2.66-2.278a.44.44 0 0 1 .25-.768l3.491-.28c.164-.013.306-.117.369-.268l1.345-3.234z' fill='%23ffd10d'/%3E%3C/svg%3E");
              width: 14px;
              height: 14px;
            }
          }

          .price {
            color: $colorM50;
          }

          .commission {
            border: 1px solid $colorP20;
            color: $colorM50;
          }
        }
      }
    }

    .description {
      gap: 12px;
      display: flex;
      padding: 16px;
      align-items: flex-start;
      border-radius: 6px;
      font-size: 13px;
      line-height: 18px;
      letter-spacing: -0.05px;
      text-align: start;
      background: $colorB10;
      color: $colorN80;

      &::before {
        content: '';
        width: 24px;
        min-width: 24px;
        height: 24px;
        display: block;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:v='https://vecta.io/nano'%3E%3Ccircle cx='12' cy='12' r='10' fill='%234fa4e5'/%3E%3Cpath d='M12 11a1 1 0 0 1 .993.884L13 12v4a1 1 0 0 1-1.993.116L11 16v-4a1 1 0 0 1 1-1zm0-4a1 1 0 1 1 0 2 1 1 0 1 1 0-2z' fill='%23fff'/%3E%3C/svg%3E");
      }
    }
  }
  .all-variants__bold {
    font-weight: 800;
  }
}
</style>
